import React, { useEffect, useState, useMemo } from "react";
import { doc, orderBy, query, updateDoc, where, setDoc, getDoc } from "firebase/firestore";
import { collection, onSnapshot } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import { Button, Upload, message, Tooltip } from "antd";
import { CopyOutlined, ShareAltOutlined } from "@ant-design/icons";
import { FaUser, FaEye, FaBox, FaSignOutAlt } from "react-icons/fa";
import { BiChip } from "react-icons/bi";
import { Link } from "react-router-dom";
import firebase from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import Shop from "./Shop";
import { formatPrice } from "../utils/priceFormatter";



const Profile = () => {
  const [panel, setPanel] = useState('info');
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const currentURL = `${window.location.origin}/`;

  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [linksData, setLinksData] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const brand = '#EB6D20';

  // Wait for auth state to be ready
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      setCurrentUser(user);
      setUser(user);
      setAuthLoading(false);
      
      // Check if user data exists in local storage
      const userData = localStorage.getItem("userData");
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          console.error("Error parsing userData:", e);
        }
      }
    });

    return () => unsubscribe(); // Cleanup the event listener on unmount
  }, []);

  // Fetch profile data when user is available
  useEffect(() => {
    if (!currentUser?.uid) {
      if (!authLoading) {
        // Auth is done but no user, so set profileData to empty to show proper message
        setProfileData(null);
      }
      return;
    }
  
    const userDocRef = doc(db, "userss", currentUser.uid);
    const linksCollectionRef = collection(userDocRef, "Links");
  
    // Query to order the Links by a timestamp field in ascending order
    const orderedLinksQuery = query(linksCollectionRef, orderBy("createdAt", "asc"));
  
    const unsubscribeUser = onSnapshot(
      userDocRef,
      (documentSnapshot) => {
        if (documentSnapshot.exists()) {
          setProfileData(documentSnapshot.data());
        } else {
          // Document doesn't exist yet, set empty object so UI can render
          setProfileData({});
        }
      },
      (error) => {
        console.error("Error fetching user profile:", error);
        setProfileData(null);
      }
    );
  
    const unsubscribeLinks = onSnapshot(
      orderedLinksQuery,
      (snapshot) => {
        const links = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLinksData(links);
      },
      (error) => {
        console.error("Error fetching links:", error);
        setLinksData([]);
      }
    );
  
    return () => {
      unsubscribeUser();
      unsubscribeLinks();
    };
  }, [currentUser, authLoading]);

  // Fetch orders from veth_payments — query by BOTH userId and payeeEmail,
  // merge results by document ID to avoid duplicates, sort newest-first.
  useEffect(() => {
    if (!currentUser?.uid) return;

    const merged = new Map(); // keyed by doc.id to deduplicate

    const sortAndSet = () => {
      const arr = Array.from(merged.values());
      arr.sort((a, b) => {
        const ts = (v) =>
          v?.createdAt?.seconds
            ? v.createdAt.seconds * 1000
            : v?.createdAt instanceof Date
            ? v.createdAt.getTime()
            : 0;
        return ts(b) - ts(a);
      });
      setOrders(arr);
    };

    // Query 1: orders where userId matches (for logged-in purchases)
    const q1 = query(
      collection(db, "veth_payments"),
      where("userId", "==", currentUser.uid)
    );
    const unsub1 = onSnapshot(q1, (snap) => {
      snap.docs.forEach((d) => merged.set(d.id, { id: d.id, ...d.data() }));
      sortAndSet();
    }, () => {});

    // Query 2: orders where payeeEmail matches (catches cases where userId was null)
    const userEmail = currentUser.email;
    const unsub2 = userEmail
      ? onSnapshot(
          query(collection(db, "veth_payments"), where("payeeEmail", "==", userEmail)),
          (snap) => {
            snap.docs.forEach((d) => merged.set(d.id, { id: d.id, ...d.data() }));
            sortAndSet();
          },
          () => {}
        )
      : () => {};

    return () => {
      unsub1();
      unsub2();
    };
  }, [currentUser]);

  const handleSignOut = () => {
    firebase
      .auth()
      .signOut()
      .then(() => {
        localStorage.removeItem("userData");
        setUser(null);
        navigate('/');
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleSaveUsername = async () => {
    const finalUsername = (editUsername || profileData?.username || '').trim().toLowerCase();
    if (!currentUser?.uid || !finalUsername) {
      message.error('Please provide a valid username.');
      return;
    }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(finalUsername)) {
      message.error('Username must be 3–20 characters (letters, numbers, underscore only).');
      return;
    }
    setUsernameSaving(true);
    try {
      const usedUsernameDoc = await getDoc(doc(db, 'usedUsernames', finalUsername));
      if (usedUsernameDoc.exists() && usedUsernameDoc.data()?.uid !== currentUser.uid) {
        message.error('Username is already taken.');
        return;
      }
      const oldUsername = profileData?.username;
      await updateDoc(doc(db, 'userss', currentUser.uid), { username: finalUsername });
      await setDoc(doc(db, 'usedUsernames', finalUsername), {
        uid: currentUser.uid,
        timestamp: new Date(),
      });
      if (oldUsername && oldUsername !== finalUsername) {
        await setDoc(doc(db, 'usedUsernames', oldUsername), { uid: null, released: true });
      }
      setProfileData((prev) => ({ ...prev, username: finalUsername }));
      message.success('Username updated!');
    } catch (error) {
      message.error('Failed to update username.');
    } finally {
      setUsernameSaving(false);
    }
  };

  useEffect(() => {
    if (profileData?.username) setEditUsername(profileData.username);
  }, [profileData?.username]);

  const handleImageUpload = async () => {
    if (profileImage) {
      setLoading(true);
      try {
        const imageRef = ref(storage, `profileImages/${currentUser.uid}`);
        await uploadBytes(imageRef, profileImage);
        const imageUrl = await getDownloadURL(imageRef);

        await updateDoc(doc(db, "userss", currentUser.uid), {
          profileImage: imageUrl,
        });

        setProfileData((prevData) => ({
          ...prevData,
          profileImage: imageUrl,
        }));

        message.success("Profile image updated successfully!");
      } catch (error) {
        message.error("Error uploading image: " + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      message.warning("Please select an image to upload.");
    }
  };

  const handleCopyLink = () => {
    const profileLink = `${currentURL}${profileData.username}`;
    navigator.clipboard.writeText(profileLink);
    message.success("Profile link copied to clipboard!");
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${profileData.username}'s Profile`,
        url: `${currentURL}${profileData.username}`,
      })
      .then(() => message.success("Profile shared successfully!"))
      .catch((error) => message.error("Error sharing profile: " + error));
    } else {
      message.warning("Share feature not supported on this browser.");
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      setProfileImage(file);
      return false;
    },
    onRemove: () => {
      setProfileImage(null);
    },
  };

  const uid = profileData?.uid
  const usernames = profileData?.username
  const email = profileData?.email
  console.log(uid, usernames, email)

  const [urls, setUrls] = useState({});

  useEffect(() => {
    if (linksData.length > 0) {
      const initialUrls = linksData.reduce((acc, link) => {
        acc[link.id] = link.url || "";
        return acc;
      }, {});
      setUrls(initialUrls);
    }
  }, [linksData]);

  const handleInputChange = (id, value) => {
    setUrls((prevUrls) => ({
      ...prevUrls,
      [id]: value,
    }));
  };

  const items = useMemo(
    () => [
      { key: 'info', label: 'Personal Information', icon: FaUser },
      { key: 'orders', label: 'Orders', icon: FaBox },
      { key: 'logout', label: 'Log out', icon: FaSignOutAlt },
    ],
    []
  );

  const onSelect = (k) => {
    if (k === 'logout') {
      return handleSignOut();
    }
    setPanel(k);
  };

  const handleSaveUrl = async (id) => {
    try {
      const linkDocRef = doc(db, "userss", currentUser.uid, "Links", id);
      await updateDoc(linkDocRef, { url: urls[id] });
      alert("URL updated successfully!");
    } catch (error) {
      console.error("Error updating URL:", error);
      alert("Failed to update URL.");
    }
  };



  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10 dark:text-white">
        Loading...
      </div>
    );
  }

  // Show message if user is not authenticated
  if (!currentUser) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10 dark:text-white">
        <p className="mb-4">You need to be signed in to view your account.</p>
        <Link className="underline text-blue-500" to="/">
          Go to sign in
        </Link>
      </div>
    );
  }

  // Show loading while profile data is being fetched
  if (profileData === null) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10 dark:text-white">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:pt-24 pt-20 dark:bg-[#000] dark:text-white">
      <div className="mb-6 mt-5">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-wide">
          VETH INTERIORS
        </h1>
        <p className="text-base sm:text-lg font-semibold text-amber-500 mt-1">
          Home of Luxury
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sidebar */}
        <aside className="md:col-span-1">
          <nav className="space-y-1">
            {items.map(({ key, label, icon: Icon }) => {
              const active = panel === key;
              return (
                <button
                  key={key}
                  onClick={() => onSelect(key)}
                  className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                    active ? 'bg-white dark:bg-[rgba(52,52,52,0.3)]' : 'bg-white dark:bg-[rgba(52,52,52,0.1)]'
                  }`}
                  style={{
                    borderColor: active ? brand : '#E5E7EB',
                    color: active ? brand : '#374151',
                  }}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <section className="md:col-span-3">
          <div className="rounded-lg border dark:border-gray-700 bg-white dark:bg-[rgba(52,52,52,0.3)] p-4 sm:p-6">
            {panel === 'info' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Personal Information</h2>
                <div className="flex items-center gap-4">
                  <Avatar
                    src={profileData?.profileImage}
                    username={profileData?.username}
                    email={profileData?.email || user?.email || ''}
                  />
                  <div>
                    <div className="text-sm font-semibold">{profileData?.username || '—'}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {profileData?.email || user?.email || '—'}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Username</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value.replace(/\s/g, ''))}
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-sm dark:bg-gray-800 dark:text-white"
                      />
                      <Button
                        onClick={handleSaveUsername}
                        loading={usernameSaving}
                        className="bg-gradient-to-r from-amber-600 to-yellow-600 border-none text-white"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                  <Field label="Email" value={profileData?.email || user?.email || '—'} />
                </div>
                <div className="space-y-3">
                  <h3 className="text-md font-semibold">Profile Image</h3>
                  <Upload {...uploadProps} listType="picture" maxCount={1}>
                    <Button type="dashed" className="dark:text-gray-300">
                      Select Profile Image
                    </Button>
                  </Upload>
                  <Button
                    type="primary"
                    className="bg-gradient-to-r from-amber-600 to-yellow-600 border-none"
                    onClick={handleImageUpload}
                    loading={loading}
                    disabled={!profileImage}
                  >
                    Upload Image
                  </Button>
                </div>
              </div>
            )}

            {panel === 'links' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Social Links</h2>
                <div className="grid gap-3">
                  {linksData.map((link) => (
                    <div
                      key={link.id}
                      className="text-start w-[100%] items-center p-5 rounded-2xl justify-center dark:bg-[rgba(52,52,52,0.3)] bg-gray-50 border dark:border-gray-700"
                    >
                      <div className="flex text-start items-center mb-2 mx-1">
                        <img src={link.image} alt="Logo" className="w-10 h-10" />
                        <p className="ml-2 font-medium">{link.name}</p>
                      </div>
                      <div className="text-start grid">
                        <input
                          type="text"
                          value={urls[link.id] || ''}
                          onChange={(e) => handleInputChange(link.id, e.target.value)}
                          className="dark:bg-[#222] bg-white border dark:border-gray-600 p-3 rounded-2xl text-sm w-[100%] dark:text-white"
                          placeholder={link.url ? link.url : `Your ${link.name} URL with https://`}
                        />
                        <button
                          onClick={() => handleSaveUrl(link.id)}
                          className="mt-2 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-2xl transition-colors"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ))}
                  {linksData.length === 0 && (
                    <div className="rounded-md border p-6 text-sm text-gray-600 dark:text-gray-400 text-center">
                      No social links added yet.
                    </div>
                  )}
                </div>
              </div>
            )}

            {panel === 'preview' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Profile Preview</h2>
                <div className="space-y-3">
                  <div className="rounded-md border dark:border-gray-700 p-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Profile Link</div>
                    <div className="flex items-center gap-2">
                      <Tooltip title="Copy Profile Link">
                        <Button
                          icon={<CopyOutlined />}
                          type="text"
                          onClick={handleCopyLink}
                          className="dark:text-white font-semibold"
                        >
                          {currentURL + profileData.username}
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      icon={<ShareAltOutlined />}
                      type="primary"
                      onClick={handleShare}
                      className="bg-gradient-to-r from-amber-600 to-yellow-600 border-none text-white"
                    >
                      Share Link
                    </Button>
                    <Link
                      to={`${currentURL}${profileData.username}`}
                      target="_blank"
                      className="bg-gradient-to-r from-amber-600 to-yellow-600 border-none items-center text-center justify-center self-center flex p-2 px-4 rounded-lg text-white"
                    >
                      <FaEye className="mr-2" />
                      Preview Profile
                    </Link>
                    <a
                      href="/setup"
                      className="bg-gradient-to-r from-amber-600 to-yellow-600 border-none items-center text-center justify-center self-center flex p-2 px-4 rounded-lg text-white"
                    >
                      <BiChip className="mr-2" />
                      Write my Chip
                    </a>
                  </div>
                </div>
              </div>
            )}

            {panel === 'orders' && (
              <OrdersPanel orders={orders} brand={brand} />
            )}

            {panel === 'activity' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Activity</h2>
                <div className="rounded-md border dark:border-gray-700 p-6 text-sm text-gray-600 dark:text-gray-400 text-center">
                  <p className="mb-4">View your activity and rewards</p>
                  <Link
                    to="/rewards"
                    className="bg-gradient-to-r from-amber-600 to-yellow-600 border-none px-4 py-2 text-sm rounded-lg text-white inline-block"
                  >
                    Go to Activity Page
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <Shop title={"me"} />
    </div>
  );
};

function Field({ label, value }) {
  return (
    <div className="rounded-md border dark:border-gray-700 p-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-sm font-medium text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}

function Avatar({ src, username, email }) {
  const initials = username?.[0]?.toUpperCase() || email?.[0]?.toUpperCase() || 'U';
  
  if (src) {
    return (
      <div className="h-16 w-16 rounded-full overflow-hidden border dark:border-gray-700">
        <img src={src} alt="Profile" className="h-full w-full object-cover" />
      </div>
    );
  }
  
  return (
    <div className="h-16 w-16 rounded-full bg-gray-200 dark:bg-gray-700 grid place-items-center text-gray-700 dark:text-gray-300 font-semibold text-lg border dark:border-gray-600">
      {initials}
    </div>
  );
}

function OrdersPanel({ orders, brand }) {
  const [tab, setTab] = useState('ongoing');
  
  const tabs = [
    { key: 'ongoing', label: 'Ongoing' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'returned', label: 'Returned' },
  ];

  // Filter orders by orderStatus (set by admin)
  const getFilteredOrders = () => {
    if (!orders || orders.length === 0) return [];

    switch (tab) {
      case 'ongoing':
        return orders.filter(order =>
          !order.orderStatus ||
          order.orderStatus === 'pending' ||
          order.orderStatus === 'on the way'
        );
      case 'completed':
        return orders.filter(order =>
          order.orderStatus === 'completed' ||
          order.orderStatus === 'delivered'
        );
      case 'cancelled':
        return orders.filter(order => order.orderStatus === 'cancelled');
      case 'returned':
        return orders.filter(order => order.orderStatus === 'returned');
      default:
        return [];
    }
  };

  const filteredOrders = getFilteredOrders();

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Orders</h2>
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              tab === t.key ? 'text-white' : ''
            }`}
            style={{
              backgroundColor: tab === t.key ? brand : 'transparent',
              borderColor: brand,
              color: tab === t.key ? 'white' : brand,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      
      {filteredOrders.length === 0 ? (
        <div className="rounded-md border dark:border-gray-700 p-6 text-sm text-gray-600 dark:text-gray-400 text-center">
          No {tab} orders yet.
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => (
            <div
              key={order.id}
              className="rounded-md border dark:border-gray-700 p-4 bg-white dark:bg-[rgba(52,52,52,0.2)]"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  {order.product_image && (
                    <img
                      src={
                        Array.isArray(order.product_image)
                          ? order.product_image[0]
                          : typeof order.product_image === 'string'
                          ? order.product_image.split(',')[0].trim()
                          : ''
                      }
                      alt={order.productPaidFor || 'Product'}
                      className="w-20 h-20 object-cover rounded-md"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-1 dark:text-white">
                      {order.productPaidFor || 'Order'}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Quantity: {order.quantity || 1}
                    </p>
                    {order.createdAt && (
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        {formatDate(order.createdAt)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {order.amount ? formatPrice(order.amount) : '—'}
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      order.orderStatus === 'completed' || order.orderStatus === 'delivered'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : order.orderStatus === 'cancelled'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : order.orderStatus === 'on the way'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                    }`}
                  >
                    {order.orderStatus === 'pending'
                      ? 'Order Placed'
                      : order.orderStatus === 'on the way'
                      ? 'On The Way'
                      : order.orderStatus === 'delivered'
                      ? 'Delivered'
                      : order.orderStatus === 'completed'
                      ? 'Completed'
                      : 'Order Placed'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Profile;
